import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, X, Lightbulb, HelpCircle, Sparkles, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { validateDriver } from './validation';
import { SentenceClassifier } from './SentenceClassifier';
import { InstructionPanel } from './InstructionPanel';
import { currentStateExamples, problemOpportunityExamples, ramificationsExamples, type Example } from './examples';
import type { BucketType } from './classification';

interface Driver {
  id: string;
  title?: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  narrative: string;
  order: number;
  desiredOutcome?: string;
  revisions: any[];
}

interface DriverEditorProps {
  driver: Driver;
  contextOfPurpose: string;
  onSave: (driver: Driver) => void;
  onCancel: () => void;
}

export const DriverEditor: React.FC<DriverEditorProps> = ({ driver, contextOfPurpose, onSave, onCancel }) => {
  const [editedDriver, setEditedDriver] = useState<Driver>(driver);
  const [showComponents, setShowComponents] = useState(true);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [showClassifier, setShowClassifier] = useState(false);
  const [classifierInitialText, setClassifierInitialText] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [showExamples, setShowExamples] = useState<{ bucket: BucketType | null }>({ bucket: null });
  const [selectedText, setSelectedText] = useState('');
  const narrativeRef = useRef<HTMLTextAreaElement>(null);

  // Auto-generate suggested narrative when components change
  useEffect(() => {
    if (editedDriver.currentState && editedDriver.problemOpportunity && editedDriver.ramifications) {
      const suggested = `${editedDriver.currentState}. ${editedDriver.problemOpportunity}. ${editedDriver.ramifications}.`;
      // Only update if narrative is empty or matches previous suggestion
      if (!editedDriver.narrative || editedDriver.narrative === editedDriver.currentState + '. ' + editedDriver.problemOpportunity + '. ' + editedDriver.ramifications + '.') {
        setEditedDriver(prev => ({ ...prev, narrative: suggested }));
      }
    }
  }, [editedDriver.currentState, editedDriver.problemOpportunity, editedDriver.ramifications]);

  const handleGenerateSuggested = () => {
    if (editedDriver.currentState && editedDriver.problemOpportunity && editedDriver.ramifications) {
      const suggested = `${editedDriver.currentState}. ${editedDriver.problemOpportunity}. ${editedDriver.ramifications}.`;
      setEditedDriver(prev => ({ ...prev, narrative: suggested }));
    }
  };

  const handleReview = () => {
    const results = validateDriver({
      contextOfPurpose,
      currentState: editedDriver.currentState,
      problemOpportunity: editedDriver.problemOpportunity,
      ramifications: editedDriver.ramifications,
      narrative: editedDriver.narrative
    });
    setValidationResults(results);
  };

  const handleSave = () => {
    onSave(editedDriver);
  };

  const handleOpenClassifier = (initialText?: string, bucket?: BucketType) => {
    setClassifierInitialText(initialText || '');
    setShowClassifier(true);
  };

  const handleInsertFromClassifier = (sentence: string, bucket: BucketType) => {
    if (bucket === 'current-state') {
      setEditedDriver(prev => ({ ...prev, currentState: prev.currentState ? `${prev.currentState} ${sentence}` : sentence }));
    } else if (bucket === 'problem-opportunity') {
      setEditedDriver(prev => ({ ...prev, problemOpportunity: prev.problemOpportunity ? `${prev.problemOpportunity} ${sentence}` : sentence }));
    } else if (bucket === 'ramifications') {
      setEditedDriver(prev => ({ ...prev, ramifications: prev.ramifications ? `${prev.ramifications} ${sentence}` : sentence }));
    }
  };

  const handleTextSelection = () => {
    const textarea = narrativeRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      if (start !== end) {
        const selected = editedDriver.narrative.substring(start, end);
        setSelectedText(selected);
      } else {
        setSelectedText('');
      }
    }
  };

  const getBucketTooltip = (bucket: 'current-state' | 'problem-opportunity' | 'ramifications'): string => {
    switch (bucket) {
      case 'current-state':
        return 'What is true? List concrete facts or observations. No interpretations. No judgments. No consequences.';
      case 'problem-opportunity':
        return 'What does the truth mean? Describe the underlying issue, friction, or opportunity created by the facts.';
      case 'ramifications':
        return 'Why does this matter? State the consequences of staying on the current path.';
    }
  };

  const getExamplesForBucket = (bucket: BucketType): Example[] => {
    switch (bucket) {
      case 'current-state':
        return currentStateExamples;
      case 'problem-opportunity':
        return problemOpportunityExamples;
      case 'ramifications':
        return ramificationsExamples;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-charcoal-50 to-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Set
          </button>
          <h1 className="text-3xl font-bold text-charcoal-900 mb-2">Edit Outcome Driver</h1>
          {contextOfPurpose && (
            <p className="text-charcoal-700 text-sm">Context: {contextOfPurpose}</p>
          )}
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Title (Optional) */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <label className="block text-sm font-semibold text-charcoal-900 mb-2">Title (Optional)</label>
            <input
              type="text"
              value={editedDriver.title || ''}
              onChange={(e) => setEditedDriver(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Short title or label for this driver"
              className="w-full p-3 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Component Fields */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-charcoal-900">Driver Components</h2>
              <button
                onClick={() => setShowComponents(!showComponents)}
                className="text-sm text-charcoal-600 hover:text-charcoal-900"
              >
                {showComponents ? 'Hide' : 'Show'} Components
              </button>
            </div>

            {showComponents && (
              <div className="space-y-6">
                {/* Current State */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal-900">Current State</h3>
                    <button className="text-charcoal-400 hover:text-charcoal-600" title={getBucketTooltip('current-state')}>
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal-600 mb-2">
                    Describe what's observably true right now. Stick to facts and objective conditions — things a camera could capture.
                  </p>
                  <textarea
                    value={editedDriver.currentState}
                    onChange={(e) => setEditedDriver(prev => ({ ...prev, currentState: e.target.value }))}
                    placeholder="Enter a factual description of the current state..."
                    className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleOpenClassifier(editedDriver.currentState, 'current-state')}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      Not sure where something belongs? Try the Sentence Classifier
                    </button>
                    <button
                      onClick={() => setShowExamples({ bucket: 'current-state' })}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      See an example
                    </button>
                  </div>
                </div>

                {/* Problem / Opportunity */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal-900">Problem / Opportunity</h3>
                    <button className="text-charcoal-400 hover:text-charcoal-600" title={getBucketTooltip('problem-opportunity')}>
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal-600 mb-2">
                    Explain the core issue created by the current state. What do the facts mean? What's the real tension or mismatch?
                  </p>
                  <textarea
                    value={editedDriver.problemOpportunity}
                    onChange={(e) => setEditedDriver(prev => ({ ...prev, problemOpportunity: e.target.value }))}
                    placeholder="Enter the core problem or opportunity..."
                    className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleOpenClassifier(editedDriver.problemOpportunity, 'problem-opportunity')}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      Not sure where something belongs? Try the Sentence Classifier
                    </button>
                    <button
                      onClick={() => setShowExamples({ bucket: 'problem-opportunity' })}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      See an example
                    </button>
                  </div>
                </div>

                {/* Ramifications / Value Judgment */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal-900">Ramifications / Value Judgment</h3>
                    <button className="text-charcoal-400 hover:text-charcoal-600" title={getBucketTooltip('ramifications')}>
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-charcoal-600 mb-2">
                    Make the stakes clear. What happens if nothing changes? Why does this matter enough to act now?
                  </p>
                  <textarea
                    value={editedDriver.ramifications}
                    onChange={(e) => setEditedDriver(prev => ({ ...prev, ramifications: e.target.value }))}
                    placeholder="Enter why this matters and what's at stake..."
                    className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      onClick={() => handleOpenClassifier(editedDriver.ramifications, 'ramifications')}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Sparkles className="h-3 w-3" />
                      Not sure where something belongs? Try the Sentence Classifier
                    </button>
                    <button
                      onClick={() => setShowExamples({ bucket: 'ramifications' })}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      See an example
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Composite Narrative */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-charcoal-900">Composite Narrative</h2>
              <button
                onClick={handleGenerateSuggested}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-2"
              >
                <Lightbulb className="h-4 w-4" />
                Update Suggested Draft
              </button>
            </div>
            <p className="text-sm text-charcoal-600 mb-4">
              This is your complete Outcome Driver narrative. Edit it directly to create a flowing paragraph or two.
            </p>
            <textarea
              ref={narrativeRef}
              value={editedDriver.narrative}
              onChange={(e) => setEditedDriver(prev => ({ ...prev, narrative: e.target.value }))}
              onSelect={handleTextSelection}
              placeholder="Your complete Outcome Driver narrative will appear here..."
              className="w-full min-h-[200px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium text-charcoal-900"
            />
            {selectedText && (
              <div className="mt-2">
                <button
                  onClick={() => handleOpenClassifier(selectedText)}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Sparkles className="h-3 w-3" />
                  Classify Selected Text
                </button>
              </div>
            )}
          </div>

          {/* Desired Outcome (Optional) */}
          <div className="bg-[var(--bg-raised)] border-2 border-primary-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-charcoal-900 mb-4">Desired Outcome (Optional)</h2>
            <p className="text-sm text-charcoal-600 mb-4">
              If this driver is addressed successfully, what will be true?
            </p>
            <textarea
              value={editedDriver.desiredOutcome || ''}
              onChange={(e) => setEditedDriver(prev => ({ ...prev, desiredOutcome: e.target.value }))}
              placeholder="Describe the desired future state..."
              className="w-full min-h-[120px] p-4 border border-charcoal-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Validation Results */}
          {validationResults && (
            <div className="bg-[var(--bg-raised)] border-2 border-[var(--mustard)] p-6 mb-6">
              <h3 className="text-lg font-bold text-charcoal-900 mb-4">Review Results</h3>
              {validationResults.diagnostics.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-charcoal-900 mb-2">What we're seeing:</h4>
                  <ul className="space-y-2">
                    {validationResults.diagnostics.map((diag: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-charcoal-700 text-sm">
                        <X className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                        <span>{diag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {validationResults.suggestions.length > 0 && (
                <div>
                  <h4 className="font-semibold text-charcoal-900 mb-2 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-primary-600" />
                    How you might tighten this up:
                  </h4>
                  <ul className="space-y-2">
                    {validationResults.suggestions.map((suggestion: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-charcoal-700 text-sm">
                        <span className="text-primary-600">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={handleReview}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 font-semibold transition-colors"
            >
              Review Driver
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 bg-sage-600 hover:bg-sage-700 text-white px-6 py-3 font-semibold transition-colors"
            >
              <Save className="h-4 w-4" />
              Save Driver
            </button>
            <button
              onClick={onCancel}
              className="flex items-center gap-2 bg-charcoal-600 hover:bg-charcoal-700 text-white px-6 py-3 font-semibold transition-colors"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>

          {/* Instructions Panel */}
          <div className="mb-6">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 border border-primary-200 transition-colors"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary-600" />
                <span className="font-semibold text-charcoal-900">View Instructions</span>
              </div>
              {showInstructions ? (
                <ChevronUp className="h-5 w-5 text-charcoal-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-charcoal-600" />
              )}
            </button>
            {showInstructions && (
              <div className="mt-4">
                <InstructionPanel />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sentence Classifier Modal */}
      <SentenceClassifier
        isOpen={showClassifier}
        onClose={() => {
          setShowClassifier(false);
          setClassifierInitialText('');
        }}
        initialSentence={classifierInitialText}
        onInsert={handleInsertFromClassifier}
      />

      {/* Examples Modal */}
      {showExamples.bucket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/60 p-4">
          <div className="bg-[var(--bg-raised)] shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-[var(--rule)]">
            <div className="sticky top-0 bg-[var(--bg-raised)] border-b border-[var(--rule)] p-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[var(--text-pri)]">
                Examples: {showExamples.bucket === 'current-state' ? 'Current State' : 
                          showExamples.bucket === 'problem-opportunity' ? 'Problem / Opportunity' : 
                          'Ramifications'}
              </h3>
              <button
                type="button"
                onClick={() => setShowExamples({ bucket: null })}
                className="text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)]"
                aria-label="Close examples"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {getExamplesForBucket(showExamples.bucket).map((example, idx) => (
                <div
                  key={idx}
                  className="p-4 border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg-raised)]/60"
                >
                  <p className="text-[var(--text-pri)] mb-2 font-medium">"{example.text}"</p>
                  {example.explanation && (
                    <p className="text-sm text-[var(--text-sec)] italic">{example.explanation}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


